import { useState } from 'react';
import Program from 'wwg-utilities/dist/collections/program';
import Provider from 'wwg-utilities/dist/collections/provider';
import getCostColorClass from 'wwg-utilities/dist/sharedFunctions/getCostColorClass';
import getCostString from 'wwg-utilities/dist/sharedFunctions/getCostString';
import getSalaryColorClass from 'wwg-utilities/dist/sharedFunctions/getSalaryColorClass';
import getSalaryStringWithOutBreak from 'wwg-utilities/dist/sharedFunctions/getSalaryWithOutBreak';
import getTimeColorClass from 'wwg-utilities/dist/sharedFunctions/getTimeColorClass';
import getDurationDataString from 'wwg-utilities/dist/sharedFunctions/getDurationDataString';
import { isSkillup } from '../../../lib/config/project/project';
import gaDataLayerEvent from '../../../lib/hooks/GA/gaDataLayerEvent';
import { useAuth } from '../../../lib/providers/authProvider';
import CareerBox from '../CareerBox';
import CostMerits from '../CostMerits';
import RenderRichText from '../RenderRichText';
import SupportMerits from '../SupportMerits';
import Testimonials from '../Testimonials';
import useLocalSalaryData from '../../../lib/hooks/localSalaryData';
import { useRegion } from '../../../lib/providers/regionProvider';

interface InfoTabsProps {
  program: Program;
}

const InfoTabs: React.FC<InfoTabsProps> = ({ program }) => {
  const [oneActive, setOneActive] = useState(false);
  const [twoActive, setTwoActive] = useState(false);
  const [threeActive, setThreeActive] = useState(true);
  const { seeker } = useAuth();
  const { region } = useRegion();
  const {
    region: { location: regionLocation },
  } = useRegion();
  const onetCode = program.primaryCareerData?.career?.onetCode || ';';
  const salaryData = useLocalSalaryData(
    onetCode,
    program,
    seeker,
    regionLocation,
  );

  // This makes our tabs accessible to a keyboard-navigating user
  const handleEnter = (e: any) => {
    if (document && document.activeElement) {
      var keycode = e.keyCode ? e.keyCode : e.which;
      if (keycode == '13') {
        const element = document.activeElement as any;
        if (element.click) {
          element.click();
        }
      }
    }
  };
  return (
    <section onKeyPress={handleEnter} className="my-8 -mx-4 md:mx-0">
      <section className="flex">
        <section
          tabIndex={0}
          role="button"
          className={`flex-1 border-t-4 text-primary p-4 cursor-pointer outline-none ${oneActive
              ? 'border-tab-active bg-off-gray'
              : 'border-md-gray bg-md-gray'
            } 
            ${getTimeColorClass(program.durationData?.timeMin?.days || 0)}
            `}
          onClick={() => {
            setOneActive(true);
            setTwoActive(false);
            setThreeActive(false);
            gaDataLayerEvent({
              event: 'tab_click',
              clicked_tab: {
                program_name: `${program.name} at ${(program.institution as Provider).name
                  }`,
                title: 'duration',
                value: getDurationDataString(program?.durationData || {}),
              },
            });
          }}
        >
          <p className="text-sm font-semi-bold">Takes</p>
          <p className="text-xl font-bold xl:text-2xl">
            {getDurationDataString(program?.durationData || {})}
          </p>
        </section>
        <section
          tabIndex={0}
          role="button"
          className={`flex-1 text-primary border-t-4 p-4 cursor-pointer outline-none ${twoActive
              ? 'border-tab-active bg-off-gray'
              : 'border-md-gray bg-md-gray'
            } 
            ${getCostColorClass(program.costData?.costMin || 0)}
            `}
          onClick={() => {
            setOneActive(false);
            setTwoActive(true);
            setThreeActive(false);
            gaDataLayerEvent({
              event: 'tab_click',
              clicked_tab: {
                program_name: `${program.name} at ${(program.institution as Provider).name
                  }`,
                title: 'cost',
                value: getCostString(program.costData || ({} as any)),
              },
            });
          }}
        >
          <p className="text-sm font-semi-bold">Costs</p>
          <p className="text-xl font-bold xl:text-2xl">
            {getCostString(program.costData || ({} as any))}
          </p>
        </section>
        <section
          tabIndex={0}
          role="button"
          className={`flex-1 text-primary cursor-pointer border-t-4 p-4 outline-none ${threeActive
              ? 'border-tab-active bg-off-gray'
              : 'border-md-gray bg-md-gray'
            } 
            ${getSalaryColorClass(
              program.primaryCareerData?.wageData?.medianAnnualSalary?.value ||
              0,
            )}
            `}
          onClick={() => {
            setOneActive(false);
            setTwoActive(false);
            setThreeActive(true);
            gaDataLayerEvent({
              event: 'tab_click',
              clicked_tab: {
                program_name: `${program.name} at ${(program.institution as Provider).name
                  }`,
                title: 'leads to',
                value: getSalaryStringWithOutBreak(
                  salaryData?.medianAnnualSalary ||
                  program.primaryCareerData?.wageData?.medianAnnualSalary
                    ?.value ||
                  0,
                ),
              },
            });
          }}
        >
          <p className="text-sm font-semi-bold">Leads to</p>
          <p className="text-xl font-bold xl:text-2xl">
            {getSalaryStringWithOutBreak(
              salaryData?.medianAnnualSalary ||
              program.primaryCareerData?.wageData?.medianAnnualSalary
                ?.value ||
              0,
            )}
          </p>
        </section>
      </section>
      <section>
        {/* Tab Content */}
        <section className="relative h-max">
          <div
            className={`bg-off-gray p-4 ${oneActive ? 'animate-fade-in  ' : ' hidden'
              }`}
          >
            <p className="font-bold ">Does it provide the support I need?</p>
            <div className="flex flex-wrap">
              <SupportMerits program={program} />
            </div>

            {Boolean(program.testimonials?.length) && (
              <Testimonials program={program} />
            )}
          </div>

          <div
            className={`bg-off-gray p-4 ${twoActive ? 'animate-fade-in  ' : ' hidden'
              }`}
          >
            <div className="flex w-full">
              <p className="font-bold text-black">
                What types of costs and discounts are involved?
              </p>
              {program.costMerits?.includes('Pays you') ? (
                <>
                  <div className="flex px-3 py-2 ml-auto text-xs font-bold text-white bg-tab-active">
                    Pays You!
                  </div>
                </>
              ) : (
                ''
              )}
            </div>

            {program.costData?.costComment && (
              <div className="my-2 font-light">
                <RenderRichText html={program.costData?.costComment} />
              </div>
            )}
            <CostMerits program={program} />
            {Boolean(
              !program.costData?.costComment &&
              !program.costMerits?.length &&
              isSkillup,
            ) && (
                <p className="-mt-2 font-light text-black">
                  We recommend asking the program directly about how they can
                  reduce your out-of-pocket costs. For example, you can ask "Do
                  you accept Federal financial aid, WIOA funding, or offer any
                  discounts or scholarships?"
                </p>
              )}
          </div>

          <div
            className={`bg-off-gray p-4 ${threeActive ? 'animate-fade-in  ' : ' hidden'
              }`}
          >
            <CareerBox program={program} region={region} />

            <div className="flex flex-wrap justify-between w-full">
              {program.careerComment && (
                <p className="mr-5 font-bold text-black whitespace-nowrap">
                  Is this a career path I'll enjoy?
                </p>
              )}
            </div>
            <div className="font-light">
              {program.careerComment ? (
                <RenderRichText html={program.careerComment}></RenderRichText>
              ) : (
                ''
              )}
            </div>
            {program.skillsComment && program.skillsComment.length ? (
              <>
                <p className="mr-5 font-bold text-black">
                  What skills and credentials will I gain?
                </p>
                <div className="font-light">
                  <RenderRichText html={program.skillsComment}></RenderRichText>
                </div>
              </>
            ) : (
              ''
            )}
          </div>
        </section>
      </section>
    </section>
  );
};

export default InfoTabs;
